import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import SchoolSelect from "@/components/site/SchoolSelect";
import PageBlocks from "@/components/site/PageBlocks";
import EditableText from "@/components/site/EditableText";

const ENGAGEMENT = [
  { id: "infos", label: "Être tenu·e informé·e" },
  { id: "creer_action", label: "Créer une action" },
  { id: "infos_region", label: "Recevoir les infos de ma région" },
  { id: "engagement_libre", label: "M'engager, peu importe la forme" },
];

const TYPES_ENSEIGNEMENT = [
  "Général",
  "Technique de transition",
  "Technique de qualification",
  "Professionnel",
  "Artistique",
  "Spécialisé",
  "CEFA",
];
const ANNEES = ["1e", "2e", "3e", "4e", "5e", "6e", "7e"];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
  const nav = useNavigate();
  const { register, user } = useAuth();
  const [form, setForm] = React.useState({
    email: "", password: "", name: "", last_name: "", age: "",
    province: "", school_code_postal: "", school_id: "", school_name: "", school_commune: "", city: "",
    type_enseignement: "", annee: "",
    is_delegue: false,
    can_be_recontacted: false, relais_local_optin: false, rgpd_consent: false,
    engagement_types: [],
  });
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => { if (user) nav("/profil"); }, [user, nav]);

  const toggle = (i) => setForm((f) => ({ ...f, engagement_types: f.engagement_types.includes(i) ? f.engagement_types.filter((x) => x !== i) : [...f.engagement_types, i] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rgpd_consent) return toast.error("Tu dois accepter le traitement RGPD pour t'inscrire.");
    if (!form.type_enseignement) return toast.error("Sélectionne ton type d'enseignement.");
    if (!form.annee) return toast.error("Sélectionne ton année d'études.");
    setLoading(true);
    try {
      await register({ ...form, age: form.age ? parseInt(form.age) : null });
      toast.success("Bienvenue dans le mouvement ! 🎉");
      nav("/profil");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <h1 className="font-display font-black text-4xl tracking-tighter uppercase">Créer mon compte</h1>
      <EditableText slug="signup" sectionKey="subtitle" className="mt-2 text-neutral-700 [&_p]:m-0">
        <p>Rejoins la communauté, gagne des badges, suis tes engagements.</p>
      </EditableText>
      <PageBlocks slug="signup" className="mt-5" />

      <button onClick={googleLogin} className="neo-btn bg-white w-full justify-center mt-6" data-testid="signup-google-btn">
        <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.12A6.6 6.6 0 0 1 5.47 12c0-.74.13-1.46.36-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.16-3.16C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.04L5.84 9.88C6.71 7.28 9.14 5.38 12 5.38z"/></svg>
        S'inscrire avec Google
      </button>

      <div className="flex items-center gap-3 my-5">
        <div className="h-px bg-black flex-1" />
        <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">ou avec un email</span>
        <div className="h-px bg-black flex-1" />
      </div>

      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="signup-form">
        <input className="neo-input" placeholder="Prénom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="signup-name" />
        <input className="neo-input" placeholder="Nom (optionnel)" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="signup-lastname" />
        <input className="neo-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="signup-email" />
        <input className="neo-input" type="password" placeholder="Mot de passe (min 8 caractères)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} data-testid="signup-password" />
        <input className="neo-input" type="number" placeholder="Âge" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="signup-age" />
        <input className="neo-input" placeholder="Ville (optionnel)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="signup-city" />

        <div className="sm:col-span-2 font-bold text-sm uppercase">Mon école</div>
        <SchoolSelect
          value={{ province: form.province, school_code_postal: form.school_code_postal, school_id: form.school_id, school_name: form.school_name, school_commune: form.school_commune }}
          onChange={(v) => setForm({ ...form, ...v })}
          required
          testIdPrefix="signup-"
        />

        <select className="neo-input" value={form.type_enseignement} onChange={(e) => setForm({ ...form, type_enseignement: e.target.value })} required data-testid="signup-type-enseignement">
          <option value="">Type d'enseignement *</option>
          {TYPES_ENSEIGNEMENT.map((n) => <option key={n}>{n}</option>)}
        </select>
        <select className="neo-input" value={form.annee} onChange={(e) => setForm({ ...form, annee: e.target.value })} required data-testid="signup-annee">
          <option value="">Année d'études *</option>
          {ANNEES.map((a) => <option key={a} value={a}>{a} secondaire</option>)}
        </select>

        <div className="sm:col-span-2 neo-card p-4 bg-cef-paper">
          <div className="font-bold text-sm uppercase mb-2">Es-tu délégué·e dans ton école ?</div>
          <div className="flex gap-2" data-testid="delegue-buttons">
            <button type="button" onClick={() => setForm({ ...form, is_delegue: true })} className={`neo-btn text-sm py-2 ${form.is_delegue ? "bg-cef-blue text-white" : "bg-white"}`} data-testid="delegue-yes">Oui</button>
            <button type="button" onClick={() => setForm({ ...form, is_delegue: false })} className={`neo-btn text-sm py-2 ${!form.is_delegue ? "bg-cef-pink text-white" : "bg-white"}`} data-testid="delegue-no">Non</button>
          </div>
        </div>

        <div className="sm:col-span-2">
          <div className="font-bold text-sm uppercase mb-2">Mon type d'engagement</div>
          <div className="flex flex-wrap gap-2">
            {ENGAGEMENT.map((e) => (
              <button key={e.id} type="button" onClick={() => toggle(e.id)} className={`chip ${form.engagement_types.includes(e.id) ? "bg-cef-pink text-white" : "bg-white"}`} data-testid={`engagement-${e.id}`}>{e.label}</button>
            ))}
          </div>
        </div>

        <label className="sm:col-span-2 flex items-start gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="mt-1" checked={form.can_be_recontacted} onChange={(e) => setForm({ ...form, can_be_recontacted: e.target.checked })} data-testid="signup-recontact" />
          <span>J'accepte d'être recontacté·e pour participer à des actions locales</span>
        </label>
        <label className="sm:col-span-2 flex items-start gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="mt-1" checked={form.relais_local_optin} onChange={(e) => setForm({ ...form, relais_local_optin: e.target.checked })} data-testid="signup-relais" />
          <span>Je souhaite devenir relais local dans mon école 📍 <span className="text-cef-green font-bold">(badge Relais local !)</span></span>
        </label>
        <label className="sm:col-span-2 flex items-start gap-2 text-sm cursor-pointer bg-cef-yellow border-2 border-black rounded-md p-3">
          <input type="checkbox" className="mt-1" checked={form.rgpd_consent} onChange={(e) => setForm({ ...form, rgpd_consent: e.target.checked })} required data-testid="signup-rgpd" />
          <span><strong>RGPD :</strong> j'accepte que mes données soient traitées par le CEF dans le cadre de cette plateforme, conformément à la réglementation européenne.</span>
        </label>

        <button type="submit" disabled={loading} className="neo-btn bg-cef-pink text-white sm:col-span-2 justify-center disabled:opacity-50" data-testid="signup-submit">
          {loading ? "…" : "Créer mon compte"}
        </button>
      </form>

      <p className="mt-5 text-sm">Déjà un compte ? <Link to="/login" className="font-bold underline">Se connecter</Link></p>
    </div>
  );
}
